import type { ReactNode } from "react"
import React from "react"
import { useTranslation } from "react-i18next"

import { Metric } from "@/components/metric/metric"
import type { BillPdfUploadSessionAnalysis } from "@/modules/data/services/use-bill-pdf-upload-session-analysis"
import { getDisplayValue } from "@/utils/value"

import {
  Alert,
  Box,
  ButtonBase,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material"

interface BillPdfPreviewAnalysisStripProps {
  analysis: BillPdfUploadSessionAnalysis | null
  isError: boolean
  isLoading: boolean
  onOpenGaps: () => void
}

interface AnalysisMetricCardProps {
  isClickable: boolean
  label: string
  onOpen?: () => void
  openLabel?: string
  testId: string
  value: ReactNode
}

const hasOpenableCount = (
  count: number | null,
  isError: boolean,
  isLoading: boolean
): boolean => !isError && !isLoading && (count ?? 0) > 0

const getCountDisplayValue = (
  count: number | null,
  isError: boolean,
  isLoading: boolean
): string => {
  if (isError || isLoading || count == null) {
    return getDisplayValue(null)
  }

  return String(count)
}

interface AnalysisMetricTextProps {
  children: ReactNode
}

const AnalysisMetricText: React.FC<AnalysisMetricTextProps> = ({
  children,
}) => (
  <Typography component="p" variant="h5">
    {children}
  </Typography>
)

interface AnalysisMetricValueProps {
  children: ReactNode
  isLoading: boolean
}

const AnalysisMetricValue: React.FC<AnalysisMetricValueProps> = ({
  children,
  isLoading,
}) => {
  if (isLoading) {
    return <Skeleton height={28} width="48%" />
  }

  return children
}

const AnalysisMetricCard: React.FC<AnalysisMetricCardProps> = ({
  isClickable,
  label,
  onOpen,
  openLabel,
  testId,
  value,
}) => {
  const metric = (
    <Box data-testid={testId} sx={{ height: "100%" }}>
      <Metric sx={{ height: "100%" }} title={label}>
        {value}
      </Metric>
    </Box>
  )

  if (!isClickable || onOpen == null) {
    return metric
  }

  return (
    <ButtonBase
      aria-label={openLabel}
      onClick={onOpen}
      sx={{
        borderRadius: 2,
        display: "block",
        height: "100%",
        textAlign: "left",
        width: "100%",
      }}
    >
      {metric}
    </ButtonBase>
  )
}

export const BillPdfPreviewAnalysisStrip: React.FC<
  BillPdfPreviewAnalysisStripProps
> = ({ analysis, isError, isLoading, onOpenGaps }) => {
  const { t } = useTranslation("data")
  const billsCount = analysis?.billsCount ?? null
  const errorsCount = analysis?.errorsCount ?? null
  const gapsCount = analysis?.gapsCount ?? null

  return (
    <Stack data-testid="bill-pdf-preview-analysis-strip" sx={{ gap: 1 }}>
      {isError ? (
        <Alert severity="error">{t("upload.billPdf.analysis.error")}</Alert>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: "repeat(3, 1fr)",
        }}
      >
        <AnalysisMetricCard
          isClickable={false}
          label={t("upload.billPdf.analysis.count")}
          testId="bill-pdf-analysis-bills"
          value={
            <AnalysisMetricValue isLoading={isLoading}>
              <AnalysisMetricText>
                {getCountDisplayValue(billsCount, isError, isLoading)}
              </AnalysisMetricText>
            </AnalysisMetricValue>
          }
        />
        <AnalysisMetricCard
          isClickable={false}
          label={t("upload.billPdf.analysis.errors")}
          testId="bill-pdf-analysis-errors"
          value={
            <AnalysisMetricValue isLoading={isLoading}>
              <AnalysisMetricText>
                {getCountDisplayValue(errorsCount, isError, isLoading)}
              </AnalysisMetricText>
            </AnalysisMetricValue>
          }
        />
        <AnalysisMetricCard
          isClickable={hasOpenableCount(gapsCount, isError, isLoading)}
          label={t("upload.billPdf.analysis.gaps")}
          openLabel={t("upload.billPdf.analysis.openGaps")}
          testId="bill-pdf-analysis-gaps"
          value={
            <AnalysisMetricValue isLoading={isLoading}>
              <AnalysisMetricText>
                {getCountDisplayValue(gapsCount, isError, isLoading)}
              </AnalysisMetricText>
            </AnalysisMetricValue>
          }
          onOpen={onOpenGaps}
        />
      </Box>
    </Stack>
  )
}
